import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';

// Configure Day.js with plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

/**
 * Converts a date input to UTC for database storage
 * @param {dayjs.Dayjs|string|Date} dateInput - Input date (Day.js object, JS Date, or ISO string)
 * @returns {string} MySQL-compatible UTC datetime string (YYYY-MM-DD HH:mm:ss)
 */
export function toUTCStorageFormat(dateInput) {
    let day;

    if (dayjs.isDayjs(dateInput)) {
        day = dateInput;
    } else if (dateInput instanceof Date) {
        day = dayjs(dateInput);
    } else if (typeof dateInput === 'string') {
        day = dayjs(dateInput); // Parses ISO or other valid formats
    } else {
        day = dayjs(); // Default to now
    }

    return day.format('YYYY-MM-DD HH:mm:ss');
}

/**
 * Converts a date input to UTC date format for queries
 * @param {dayjs.Dayjs|string|Date} dateInput - Input date (Day.js object, JS Date, or ISO string)
 * @returns {string} MySQL-compatible UTC date string (YYYY-MM-DD)
 */
export function toUTCDateFormat(dateInput) {
    let day;

    if (dayjs.isDayjs(dateInput)) {
        day = dateInput;
    } else if (dateInput instanceof Date) {
        day = dayjs(dateInput);
    } else if (typeof dateInput === 'string') {
        day = dayjs(dateInput);
    } else {
        day = dayjs();
    }

    return day.utc().format('YYYY-MM-DD');
}

/**
 * Converts a UTC database datetime to local user timezone
 * @param {string} utcDbString - MySQL datetime string in UTC (e.g., '2025-04-27 04:00:00')
 * @param {string} [format] - Optional output format (e.g., 'YYYY-MM-DD')
 * @param {string} [timezone] - Target timezone (defaults to system timezone)
 * @returns {string|dayjs.Dayjs} Formatted string or Day.js object in local timezone
 */
export function fromDbToLocal(utcDbString, format = null, timezone = Intl.DateTimeFormat().resolvedOptions().timeZone) {
    const day = dayjs.utc(utcDbString).tz(timezone);
    return format ? day.format(format) : day;
}

/**
 * Get current UTC time in storage format
 * @returns {string} Current time in MySQL-compatible UTC format (YYYY-MM-DD HH:mm:ss)
 */
// Helper function to get current local time without UTC conversion
export const getCurrentLocalTimeForStorage = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

/**
 * Parses a custom formatted date string
 * @param {string|Date|dayjs.Dayjs} dateInput - Input to parse
 * @param {string} formatString - The format to parse against (e.g., 'DD/MM/YYYY', 'hh:mm A')
 * @returns {dayjs.Dayjs} Parsed Day.js object (invalid if parsing fails)
 */
export function parseCustomFormat(dateInput, formatString) {
    if (dayjs.isDayjs(dateInput)) {
        return dateInput;
    } else if (dateInput instanceof Date) {
        return dayjs(dateInput);
    } else if (typeof dateInput === 'string') {
        return dayjs(dateInput, formatString);
    } else {
        return dayjs.invalid();
    }
}

/**
 * Format a date string into separated date and time components without timezone conversion
 * @param {string} dateString - Date string in local format (no UTC conversion)
 * @param {Object} [options] - Formatting options (leave blank for defaults)
 * @param {string} [options.dateFormat='MMM D, YYYY'] - Format string for date
 * @param {string} [options.timeFormat='HH:mm A'] - Format string for time
 * @param {string} [options.locale='en'] - Locale for formatting (requires dayjs/locale plugin)
 * @returns {Object} Object with separated date and time strings
 */
export function formatDateTime(dateString, options = {}) {
    // Return placeholders for empty input
    if (!dateString) return { date: '--', time: '--' };

    const { dateFormat = 'MMM D, YYYY', timeFormat = 'HH:mm', locale = 'en' } = options;

    try {
        // Create dayjs object from string without UTC conversion - use as local time
        let dt = dayjs(dateString);

        // Return if invalid date
        if (!dt.isValid()) {
            return { date: '--', time: '--' };
        }

        // Format date and time components
        return {
            date: dt.format(dateFormat),
            time: dt.format(timeFormat),
            // Include the dayjs object for additional formatting if needed
            dayjs: dt
        };
    } catch (error) {
        console.error('Error formatting date:', error);
        return { date: '--', time: '--' };
    }
}
